import type { PollRead } from '@models/api/poll';
import type { PollMenuRow, PollMenuViewer } from '@components/polls/pollMenuRows';

/** The rows a live poll's ⋯ menu can hold (a subset of a poll's; no Reopen, Back is a host control). */
export type LiveMenuRow = Extract<
  PollMenuRow,
  'pin' | 'unpin' | 'bookmark' | 'edit' | 'end' | 'report' | 'delete'
>;

/**
 * The ⋯ menu of a live poll, in order, by role and state (TECH-867 § ⋯ menu on a live poll;
 * CONTRACT § 10 N27, F8):
 * - author: before Start, Edit (`can_edit`) and Delete; live or ended, Bookmark and Delete. Start,
 *   Next, Back and End live only in the host controls; never Report;
 * - platform admin (not the author): End poll now (moderation, `can_end`), Report, Delete;
 * - Space admin or moderator, and member: Bookmark, Report ("Remove from {Space}" has no route, F8);
 * - Pin poll: a Space admin in that Space's feed, once started (before Start it is in no feed but the
 *   author's); never the platform-admin role alone (N27).
 * Report sits above Delete.
 */
export const liveMenuRows = (
  poll: Pick<PollRead, 'live_state' | 'can_edit' | 'can_end'>,
  viewer: PollMenuViewer,
): LiveMenuRow[] => {
  const rows: LiveMenuRow[] = [];
  const started = poll.live_state !== 'not_started';
  if (started && viewer.inSpaceFeed && viewer.isSpaceAdmin) {
    rows.push(viewer.isPinned ? 'unpin' : 'pin');
  }
  if (viewer.isAuthor) {
    if (!started) {
      if (poll.can_edit) rows.push('edit');
    } else {
      rows.push('bookmark');
    }
    rows.push('delete');
    return rows;
  }
  if (viewer.isPlatformAdmin) {
    if (poll.can_end) rows.push('end');
    rows.push('report', 'delete');
    return rows;
  }
  rows.push('bookmark', 'report');
  return rows;
};
